import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import type { ReactNode } from "react";
import React from "react";
import { Table } from "~/components/common/parts/table/Table";
import { TableBody } from "~/components/common/parts/table/TableBody";
import { TableHeader } from "~/components/common/parts/table/TableHeader";
import { TodoTemplateListRow } from "~/components/common/todo_templates/TodoTemplateListRow";
import { TodoTemplateModal } from "~/components/common/todo_templates/TodoTemplateModal";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useTodoTemplates } from "~/hooks/useTodoTemplates";
import type { DealType } from "~/models/Deal";
import type { Subtype } from "~/models/Subtype";
import type { TodoTemplate } from "~/models/TodoTemplate";
import { filterTodoTemplatesByDealType } from "~/models/TodoTemplate";
import Styles from "./TodoTemplateList.module.scss";

type Props = DeepReadonly<{
	dealType: DealType;
	subtype?: Subtype | null;
}>;

export const TodoTemplateList: React.FC<Props> = ({
	dealType,
	subtype = null
}: Props) => {
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const { data: todoTemplates, mutate: mutateTodoTemplates } =
		useTodoTemplates().useIndex();
	const handleMutate = React.useCallback(async () => {
		await mutateTodoTemplates?.();
	}, [mutateTodoTemplates]);
	const filteredTodoTemplates = React.useMemo(() => {
		const templates = filterTodoTemplatesByDealType(
			dealType,
			subtype?.id ?? null,
			todoTemplates
		);

		// ステータス順に並び替える
		return templates.sort((a, b) => a.statusId - b.statusId);
	}, [dealType, subtype, todoTemplates]);

	const renderRow = React.useCallback(
		(todoTemplate: TodoTemplate): ReactNode => (
			<TodoTemplateListRow
				dealType={dealType}
				key={todoTemplate.id}
				subtype={subtype}
				todoTemplate={todoTemplate}
			/>
		),
		[dealType, subtype]
	);
	return (
		<div>
			<Table>
				<TableHeader>
					<th>作成ステータス</th>
					<th>タイトル</th>
					<th>操作</th>
				</TableHeader>
				<TableBody>{filteredTodoTemplates.map(renderRow)}</TableBody>
			</Table>
			<div className={Styles.ButtonArea}>
				<Button height="default" onClick={handleOpen} variant="secondary">
					TODOテンプレートの追加
				</Button>
			</div>
			<TodoTemplateModal
				dealType={dealType}
				handleClose={handleClose}
				handleMutate={handleMutate}
				isOpen={isOpen}
				subtypeId={subtype?.id ?? null}
			/>
		</div>
	);
};
